import { useNavigate, useParams } from "react-router-dom";
import { useRef, useState } from "react";
import { useFormFields } from "../../../../hooks/useFormFields";
import { useProductVariants as useProductDetails } from "../../../products/hooks/useProduct";
import { Input } from "../../../../components/common/input/input";
import { Button } from "../../../../components/common/button/button";
import { PageContentSkeleton } from "../../../../components/common/page-content-skeleton";
import { AppCard } from "../../../../components/common/card";
import { Text } from "../../../../components/common/typography";
import { Breadcrumbs } from "../../../../components/common/breadcrumbs";
import { PageContainer } from "../../../../components/common/page-container";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useCreateChangeRequest } from "../../hooks/useChangeRequestMutations";
import { useProductImageUpload } from "../../hooks/useProductImageUpload";
import { getChangeRequestsListPath } from "../../data/change-request-list-path";
import { useMePartner } from "../../../../swr";
import {
  CreateChangeRequestDtoTypeEnum,
  type CreateChangeRequestDto,
} from "../../../../api";

type ProductFormFields = {
  name: string;
  description: string;
  listPrice: string;
  defaultCode: string;
  note: string;
};

export function EditProductRequestPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { isBuyer, isSeller } = useMePartner();
  const listPath = getChangeRequestsListPath({ isBuyer, isSeller });
  const productIdNum = Number(productId);
  const isValidId = Number.isFinite(productIdNum) && productIdNum > 0;

  const { data, isLoading, error } = useProductDetails(
    isValidId ? productIdNum : 0,
  );
  const product = data?.product;

  const initialValues: ProductFormFields = {
    name: product?.name ?? "",
    description: product?.description ?? "",
    listPrice:
      product?.listPrice !== null && product?.listPrice !== undefined
        ? String(product.listPrice)
        : "",
    defaultCode: product?.defaultCode ?? "",
    note: "",
  };

  const { formFields, createChangeHandler, setFormFields } =
    useFormFields<ProductFormFields>(initialValues);

  const [pendingObjectKey, setPendingObjectKey] = useState<string | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isUploading, progress, upload } = useProductImageUpload();

  const buildProposedChanges = (): Record<string, unknown> => {
    if (!product) return {};
    const diff: Record<string, unknown> = {};

    if (formFields.name.trim() !== (product.name ?? "")) {
      diff.name = formFields.name.trim();
    }
    if (formFields.description.trim() !== (product.description ?? "")) {
      diff.description = formFields.description.trim();
    }
    const currentList =
      product.listPrice === null || product.listPrice === undefined
        ? ""
        : String(product.listPrice);
    if (formFields.listPrice.trim() !== currentList) {
      const parsed = parseFloat(formFields.listPrice);
      if (!Number.isNaN(parsed)) diff.listPrice = parsed;
    }
    if (formFields.defaultCode.trim() !== (product.defaultCode ?? "")) {
      diff.defaultCode = formFields.defaultCode.trim();
    }
    if (pendingObjectKey) {
      diff.image = pendingObjectKey;
    }
    return diff;
  };

  const buildDto = (): CreateChangeRequestDto => ({
    type: CreateChangeRequestDtoTypeEnum.ProductUpdate,
    proposedChanges: buildProposedChanges(),
    targetProductId: productIdNum,
    note: formFields.note.trim() || undefined,
  });

  const draft = useCreateChangeRequest(buildDto);
  const submit = useCreateChangeRequest(buildDto, { submitAfter: true });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const localPreview = URL.createObjectURL(file);
    setPendingPreviewUrl(localPreview);
    const objectKey = await upload(file);
    if (objectKey) {
      setPendingObjectKey(objectKey);
    } else {
      setPendingPreviewUrl(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemovePending = () => {
    if (pendingPreviewUrl) URL.revokeObjectURL(pendingPreviewUrl);
    setPendingObjectKey(null);
    setPendingPreviewUrl(null);
  };

  const handleResetForm = () => {
    setFormFields(initialValues);
    handleRemovePending();
  };

  const diffCount = Object.keys(buildProposedChanges()).length;
  const isBusy = draft.isLoading || submit.isLoading || isUploading;

  if (!isValidId) {
    return (
      <PageContainer>
        <AppCard title="Invalid product" color="bg-bgDefault" className="w-full">
          <Text variant="body" className="text-accentError">
            The product ID in the URL is invalid.
          </Text>
        </AppCard>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PageContentSkeleton cardCount={2} actionCount={2} />
      </PageContainer>
    );
  }

  if (error || !product) {
    return (
      <PageContainer>
        <AppCard title="Error" color="bg-bgDefault" className="w-full">
          <Text variant="body" className="text-accentError">
            Failed to load product. Please try again later.
          </Text>
        </AppCard>
      </PageContainer>
    );
  }

  const displayedImage =
    pendingPreviewUrl ??
    (product.image && product.image.startsWith("http") ? product.image : null);

  return (
    <PageContainer>
      <div className="flex flex-col gap-6 w-full min-w-0">
        <Breadcrumbs
          items={[
            { label: "Home", path: AppRoutes.INDEX },
            { label: "My Change Requests", path: listPath },
            { label: `Edit ${product.name}` },
          ]}
        />

        <AppCard
          title={`Edit "${product.name}"`}
          color="bg-bgDefault"
          className="w-full"
        >
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (diffCount === 0) return;
              void submit.submitHandler();
            }}
          >
            <Text variant="bodySm" className="text-textMuted">
              Only modified fields will be sent for admin review. Approved changes are
              applied to the Odoo product template.
            </Text>

            <div className="flex gap-4 items-start">
              <div className="w-32 h-32 flex-none rounded-lg border border-borderDefault overflow-hidden bg-uiSecondary flex items-center justify-center">
                {displayedImage ? (
                  <img
                    src={displayedImage}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Text variant="bodyXs" className="text-textMuted">
                    No image
                  </Text>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => void handleFileSelect(e)}
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isBusy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploading
                    ? `Uploading ${Math.round(progress * 100)}%...`
                    : "Replace image"}
                </Button>
                {pendingObjectKey && (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={isBusy}
                    onClick={handleRemovePending}
                  >
                    Keep existing image
                  </Button>
                )}
                <Text variant="bodyXs" className="text-textMuted">
                  JPEG, PNG, or WebP. Max 10MB.
                </Text>
              </div>
            </div>

            <Input
              label="Name"
              value={formFields.name}
              onChange={createChangeHandler("name")}
            />
            <Input
              label="SKU / product code"
              value={formFields.defaultCode}
              onChange={createChangeHandler("defaultCode")}
            />
            <Input
              label="List price"
              type="number"
              value={formFields.listPrice}
              onChange={createChangeHandler("listPrice")}
            />
            <Input
              label="Description"
              value={formFields.description}
              onChange={createChangeHandler("description")}
            />
            <Input
              label="Note to admin (optional)"
              value={formFields.note}
              onChange={createChangeHandler("note")}
              helperText="Short context for the reviewer."
            />

            {diffCount === 0 && (
              <Text variant="bodyXs" className="text-textMuted">
                No changes yet — edit a field or replace the image to enable submit.
              </Text>
            )}

            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                type="submit"
                variant="primary"
                disabled={isBusy || diffCount === 0}
              >
                {submit.isLoading ? "Submitting..." : "Submit for review"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={isBusy || diffCount === 0}
                onClick={() => void draft.submitHandler()}
              >
                {draft.isLoading ? "Saving..." : "Save draft"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={isBusy}
                onClick={handleResetForm}
              >
                Reset
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={isBusy}
                onClick={() => navigate(listPath)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </AppCard>
      </div>
    </PageContainer>
  );
}
